// AiSkillsSection — Settings/Admin tab for managing AI Skills
// Global skills: admin-only CRUD via /api/admin/skills
// Org skills: org member CRUD via /api/charlie/ai-skills
// Non-admins can import (copy) global skills into their org

import { ChevronDown, ChevronUp, Download, FlaskConical, Globe, Loader2, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import {
  AiSkill,
  createAiSkill,
  createGlobalSkill,
  deleteAiSkill,
  deleteGlobalSkill,
  getAiSkills,
  updateAiSkill,
  updateGlobalSkill,
} from '../../services/aiSkillsService';
import { GlobalProviderAssignments } from './GlobalProviderAssignments';
import { ProviderSkillAssignments } from './ProviderSkillAssignments';
import { TestSkillModal } from './TestSkillModal';

// Inline form for creating / editing a skill
type SkillFormProps = {
  initial?: Partial<AiSkill>;
  onSubmit: (values: { skill_name: string; system_prompt_md: string; default_flag: boolean }) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
};

const SkillForm = ({ initial, onSubmit, onCancel, submitLabel }: SkillFormProps) => {
  const [skillName, setSkillName] = useState(initial?.skill_name ?? '');
  const [prompt, setPrompt] = useState(initial?.system_prompt_md ?? '');
  const [defaultFlag, setDefaultFlag] = useState(initial?.default_flag ?? false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({ skill_name: skillName, system_prompt_md: prompt, default_flag: defaultFlag });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 border border-border rounded-lg bg-muted/20">
      <div>
        <label className="block text-sm font-medium mb-1">Skill Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          required
          value={skillName}
          onChange={(e) => setSkillName(e.target.value)}
          placeholder="e.g. Concise Writer"
          className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">System Prompt (Markdown) <span className="text-red-500">*</span></label>
        <textarea
          required
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={6}
          placeholder="Write the instruction set for the AI in Markdown..."
          className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          id="default-flag"
          type="checkbox"
          checked={defaultFlag}
          onChange={(e) => setDefaultFlag(e.target.checked)}
          className="w-4 h-4 accent-primary"
        />
        <label htmlFor="default-flag" className="text-sm">Use as default in AI generation</label>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted/50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// A single skill row
type SkillRowProps = {
  skill: AiSkill;
  isAdmin: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onImport?: () => void;
  onTest?: () => void;
  isDeleting: boolean;
};

const SkillRow = ({ skill, isAdmin, onEdit, onDelete, onImport, onTest, isDeleting }: SkillRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const isGlobal = skill.tenant_id === null;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{skill.skill_name}</span>
            {skill.default_flag && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">Default</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isGlobal ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'}`}>
              {isGlobal ? 'Global' : 'Custom'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {onTest && (
            <button
              onClick={onTest}
              title="Test Skill"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted/50"
            >
              <FlaskConical className="w-3.5 h-3.5" />
              Test
            </button>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted/50"
          >
            View prompt {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {/* Global skill: show Import for non-admins, show Edit/Delete for admins */}
          {isGlobal && !isAdmin && onImport && (
            <button
              onClick={onImport}
              title="Import to your org"
              className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          {isGlobal && isAdmin && onEdit && (
            <button onClick={onEdit} title="Edit" className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground">
              <Pencil className="w-4 h-4" />
            </button>
          )}
          {isGlobal && isAdmin && onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              title="Delete"
              className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950 text-muted-foreground hover:text-red-600 disabled:opacity-50"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          )}

          {/* Org skill: always show Edit/Delete */}
          {!isGlobal && onEdit && (
            <button onClick={onEdit} title="Edit" className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground">
              <Pencil className="w-4 h-4" />
            </button>
          )}
          {!isGlobal && onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              title="Delete"
              className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950 text-muted-foreground hover:text-red-600 disabled:opacity-50"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-0">
          <pre className="text-xs font-mono bg-muted/40 rounded p-3 whitespace-pre-wrap break-words max-h-60 overflow-y-auto">
            {skill.system_prompt_md}
          </pre>
        </div>
      )}
    </div>
  );
};

// Confirmation dialog for deleting global skills
type ConfirmDeleteDialogProps = {
  skillName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
};

const ConfirmDeleteDialog = ({ skillName, onConfirm, onCancel, isDeleting }: ConfirmDeleteDialogProps) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-background border border-border rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-2">Delete Global Skill</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Are you sure you want to delete <strong>&quot;{skillName}&quot;</strong>? This skill is visible to all tenants and this action cannot be undone.
      </p>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted/50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
        >
          {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
          Delete
        </button>
      </div>
    </div>
  </div>
);

// Main section component
export const AiSkillsSection = () => {
  const { toast } = useToast();
  const { permissions } = useUserPermissions();
  const isAdmin = permissions?.isOrgAdmin || permissions?.isPlatformAdmin || false;

  const [skills, setSkills] = useState<AiSkill[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state: null = hidden, 'new' = new org skill, 'new-global' = new global skill
  type FormMode = null | 'new' | 'new-global' | { skillId: string; isGlobal?: boolean; prefill?: Partial<AiSkill> };
  const [formMode, setFormMode] = useState<FormMode>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [testingSkill, setTestingSkill] = useState<AiSkill | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AiSkill | null>(null);

  const loadSkills = async () => {
    setLoading(true);
    try {
      const data = await getAiSkills();
      setSkills(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load skills';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSkills();
  }, []);

  // Create org-scoped skill
  const handleCreate = async (values: { skill_name: string; system_prompt_md: string; default_flag: boolean }) => {
    try {
      await createAiSkill(values);
      toast({ title: 'Skill created', description: `"${values.skill_name}" has been added.` });
      setFormMode(null);
      await loadSkills();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create skill';
      toast({ title: 'Error', description: message, variant: 'destructive' });
      throw err;
    }
  };

  // Create global skill (admin only, uses admin endpoint)
  const handleCreateGlobal = async (values: { skill_name: string; system_prompt_md: string; default_flag: boolean }) => {
    try {
      await createGlobalSkill(values);
      toast({ title: 'Global skill created', description: `"${values.skill_name}" is now available to all tenants.` });
      setFormMode(null);
      await loadSkills();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create global skill';
      toast({ title: 'Error', description: message, variant: 'destructive' });
      throw err;
    }
  };

  // Update skill — routes to admin or org endpoint based on skill type
  const handleUpdate = async (
    id: string,
    isGlobal: boolean,
    values: { skill_name: string; system_prompt_md: string; default_flag: boolean }
  ) => {
    try {
      if (isGlobal) {
        await updateGlobalSkill(id, values);
      } else {
        await updateAiSkill(id, values);
      }
      toast({ title: 'Skill updated', description: `"${values.skill_name}" has been saved.` });
      setFormMode(null);
      await loadSkills();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update skill';
      toast({ title: 'Error', description: message, variant: 'destructive' });
      throw err;
    }
  };

  // Delete skill — routes to admin or org endpoint
  const handleDelete = async (skill: AiSkill) => {
    const isGlobal = skill.tenant_id === null;

    // Global skills need confirmation dialog
    if (isGlobal) {
      setConfirmDelete(skill);
      return;
    }

    setDeletingId(skill.id);
    try {
      await deleteAiSkill(skill.id);
      setSkills((prev) => prev.filter((s) => s.id !== skill.id));
      toast({ title: 'Skill deleted' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete skill';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  // Confirmed delete for global skills
  const handleConfirmDeleteGlobal = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await deleteGlobalSkill(confirmDelete.id);
      setSkills((prev) => prev.filter((s) => s.id !== confirmDelete.id));
      toast({ title: 'Global skill deleted' });
      setConfirmDelete(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete global skill';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  // Import: copy global skill into org
  const handleImport = (skill: AiSkill) => {
    setFormMode({ skillId: 'import', prefill: { skill_name: skill.skill_name, system_prompt_md: skill.system_prompt_md } });
  };

  const handleImportSubmit = async (values: { skill_name: string; system_prompt_md: string; default_flag: boolean }) => {
    try {
      await createAiSkill(values);
      toast({ title: 'Skill imported', description: 'Skill imported to your organisation.' });
      setFormMode(null);
      await loadSkills();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to import skill';
      toast({ title: 'Error', description: message, variant: 'destructive' });
      throw err;
    }
  };

  const globalSkills = skills.filter((s) => s.tenant_id === null);
  const orgSkills = skills.filter((s) => s.tenant_id !== null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading skills…</span>
      </div>
    );
  }

  const isImportMode = typeof formMode === 'object' && formMode !== null && (formMode as { skillId: string }).skillId === 'import';
  const editTarget = typeof formMode === 'object' && formMode !== null && (formMode as { skillId: string }).skillId !== 'import'
    ? skills.find((s) => s.id === (formMode as { skillId: string }).skillId)
    : undefined;
  const editIsGlobal = typeof formMode === 'object' && formMode !== null ? (formMode as { isGlobal?: boolean }).isGlobal ?? false : false;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6" />
            AI Skills
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Reusable instructions injected into the AI context during document generation.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button
              onClick={() => setFormMode('new-global')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
            >
              <Globe className="w-4 h-4" />
              New Global Skill
            </button>
          )}
          <button
            onClick={() => setFormMode('new')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            New Skill
          </button>
        </div>
      </div>

      {/* New org skill form */}
      {formMode === 'new' && (
        <SkillForm
          onSubmit={handleCreate}
          onCancel={() => setFormMode(null)}
          submitLabel="Create Skill"
        />
      )}

      {/* New global skill form (admin only) */}
      {formMode === 'new-global' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
            <Globe className="w-4 h-4" />
            <span className="font-medium">Creating a Global Skill — visible to all tenants</span>
          </div>
          <SkillForm
            onSubmit={handleCreateGlobal}
            onCancel={() => setFormMode(null)}
            submitLabel="Create Global Skill"
          />
        </div>
      )}

      {/* Import form */}
      {isImportMode && (
        <SkillForm
          initial={(formMode as { skillId: string; prefill?: Partial<AiSkill> }).prefill}
          onSubmit={handleImportSubmit}
          onCancel={() => setFormMode(null)}
          submitLabel="Import to My Org"
        />
      )}

      {/* Global Skills */}
      {globalSkills.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Global Skills</h3>
            <span className="text-xs text-muted-foreground">({globalSkills.length})</span>
          </div>
          {globalSkills.map((skill) => (
            <div key={skill.id}>
              {editTarget?.id === skill.id ? (
                <SkillForm
                  initial={skill}
                  onSubmit={(v) => handleUpdate(skill.id, editIsGlobal, v)}
                  onCancel={() => setFormMode(null)}
                  submitLabel="Save Changes"
                />
              ) : (
                <SkillRow
                  skill={skill}
                  isAdmin={isAdmin}
                  onEdit={isAdmin ? () => setFormMode({ skillId: skill.id, isGlobal: true }) : undefined}
                  onDelete={isAdmin ? () => handleDelete(skill) : undefined}
                  onImport={!isAdmin ? () => handleImport(skill) : undefined}
                  onTest={() => setTestingSkill(skill)}
                  isDeleting={deletingId === skill.id}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Global Default Assignments (admin only) */}
      {isAdmin && (
        <div className="pt-4 border-t border-border">
          <GlobalProviderAssignments />
        </div>
      )}

      {/* Org Skills */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Your Organisation&apos;s Skills
          <span className="text-xs font-normal ml-2">({orgSkills.length})</span>
        </h3>
        {orgSkills.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
            No custom skills yet. Click <strong>New Skill</strong> to create one, or import a global skill.
          </div>
        ) : (
          orgSkills.map((skill) => (
            <div key={skill.id}>
              {editTarget?.id === skill.id ? (
                <SkillForm
                  initial={skill}
                  onSubmit={(v) => handleUpdate(skill.id, false, v)}
                  onCancel={() => setFormMode(null)}
                  submitLabel="Save Changes"
                />
              ) : (
                <SkillRow
                  skill={skill}
                  isAdmin={isAdmin}
                  onEdit={() => setFormMode({ skillId: skill.id, isGlobal: false })}
                  onDelete={() => handleDelete(skill)}
                  onTest={() => setTestingSkill(skill)}
                  isDeleting={deletingId === skill.id}
                />
              )}
            </div>
          ))
        )}
      </div>

      {/* Provider Skill Assignments */}
      <div className="pt-4 border-t border-border">
        <ProviderSkillAssignments />
      </div>

      {/* Test Skill Modal */}
      {testingSkill && (
        <TestSkillModal
          skill={testingSkill}
          onClose={() => setTestingSkill(null)}
        />
      )}

      {/* Confirm Delete Dialog for Global Skills */}
      {confirmDelete && (
        <ConfirmDeleteDialog
          skillName={confirmDelete.skill_name}
          onConfirm={handleConfirmDeleteGlobal}
          onCancel={() => { setConfirmDelete(null); setDeletingId(null); }}
          isDeleting={deletingId === confirmDelete.id}
        />
      )}
    </div>
  );
};
